import { CallableMethods, GlobalMethodNames, OrderMethodNames, WalletAddressMethodNames } from '@btcdirect/embeddable-loader';
import { OfferParameters, dispatchSentryException, getOfferParameters } from '@btcdirect/functions';
import { useUserAuthentication } from '@btcdirect/shared/src/composables/auth/user-authentication';
import { useLocale } from '@btcdirect/shared/src/composables/util/locale';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import { usePartnerInfo } from '@btcdirect/shared/src/composables/util/partner-info';
import { ExternalWalletAddress, InstanceParameters, MethodParameters } from '@btcdirect/shared/src/types';
import {
    QuoteFrom,
    WalletAddress,
    useAdyenSessionStore,
    useAuthenticationStore,
    useClientInfoStore,
    useConfigurationStore,
    useCurrenciesStore,
    useOfferStore,
    usePaymentMethodsStore,
    useQuoteConfirmationStore,
    useQuoteStore,
    useWalletAddressesStore
} from '@btcdirect/stores';
import { WalletAddressVerificationStatus } from 'packages/stores/src/wallets';

const { warn } = useLogger();

/**
 * Global methods that are available for all embeddables
 */
export const GlobalMethods: CallableMethods = {
    [GlobalMethodNames.LOCALE]: provideLocale,
    [GlobalMethodNames.PARTNER_INFO]: providePartnerInfo,
    [GlobalMethodNames.ONE_TIME_LOGIN]: provideOneTimeLogin,
    [GlobalMethodNames.LOGOUT]: () => useUserAuthentication().logout()
};

/**
 * Wallet address related methods
 */
export const WalletAddressMethods: CallableMethods = {
    [WalletAddressMethodNames.ADDRESSES]: provideWalletAddresses,
    [WalletAddressMethodNames.ADDRESS_SELECT]: provideSelectedWalletAddress,
    [WalletAddressMethodNames.ADDRESS_PROVIDE]: provideWalletAddressForVerification
};

/**
 * Methods that are available for embeddables that process orders
 */
export const OrderMethods: CallableMethods = {
    [OrderMethodNames.ADDRESS_CANCEL]: cancelWalletAddressConfirmation,
    [OrderMethodNames.ADDRESS_CONFIRM]: provideWalletAddressConfirmation,
    [OrderMethodNames.ADYEN_CALLBACK]: provideAdyenParametersToWidget,
    [OrderMethodNames.CALLBACK]: provideCallbackUrl,
    [OrderMethodNames.C2F_ORDER_CANCEL]: cancelCurrentOrder,
    [OrderMethodNames.CURRENCIES]: provideCurrencies,
    [OrderMethodNames.F2C_ORDER_CANCEL]: cancelCurrentOrder,
    [OrderMethodNames.ORDER_INFO]: provideOrder,
    [OrderMethodNames.CONFIRMED_ORDER]: provideConfirmedOrder,
    [OrderMethodNames.PAYMENT_METHOD]: provideSelectedPaymentMethod,
    [OrderMethodNames.TRANSACTION_CONFIRM]: provideTransactionId,
    [OrderMethodNames.ATTEMPT_DIRECT_CONFIRM]: attemptDirectConfirm,
    [OrderMethodNames.SHOW_ADDITIONAL_FEES]: showAdditionalFees // Deprecated method
};

/**
 * Methods that are available for embeddables that process offers
 * @param method
 * @param requiredParams
 * @param callParams
 */
export const OfferMethods: CallableMethods = {
    [OrderMethodNames.SET_PARAMETERS]: provideOfferParameters
};

/**
 * Validation of the required parameters for the method to call
 * TODO: Remove export after not using it in widgets/fiat-to-coin/main.ts
 */
export function validateRequiredParameters(method: string, requiredParams: string[], callParams: MethodParameters): boolean {
    if (requiredParams.every((parameter) => parameter in callParams)) {
        return true;
    }

    warn(`Method "${method}" is missing required parameter(s)`, `Required parameters are "${requiredParams.join(', ')}"`);
    return false;
}

function provideLocale(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(GlobalMethodNames.LOCALE, ['locale'], callParameters)) {
        return;
    }

    // TODO: Should be config store
    const { setLocale } = useLocale();

    setLocale(callParameters.locale);
}

function providePartnerInfo(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(GlobalMethodNames.PARTNER_INFO, ['id', 'name'], callParameters)) {
        return;
    }

    // TODO: Should be partner store
    const { setPartnerInfo } = usePartnerInfo();

    const partnerId = 'string' === typeof callParameters.id ? parseInt(callParameters.id) : callParameters.id;
    setPartnerInfo(partnerId, callParameters.name);
}

function provideOneTimeLogin(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(GlobalMethodNames.ONE_TIME_LOGIN, ['userToken', 'userUuid'], callParameters)) {
        return;
    }

    const store = useAuthenticationStore();
    store.setOneTimeAuthentication(callParameters.userToken, callParameters.userUuid);
}

function provideCurrencies(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.CURRENCIES, ['crypto', 'fiat'], callParameters)) {
        return;
    }

    const store = useCurrenciesStore();

    if (!store.currencyPairs?.length) {
        store.setCryptoCurrency(callParameters.crypto);
        store.setFiatCurrency(callParameters.fiat);
        return;
    }

    const crypto = store.cryptoCurrencies.find((item) => item === callParameters.crypto);
    !!crypto ? store.setCryptoCurrency(crypto) : warn(`Could not set crypto currency`, `${callParameters.crypto} is not found in the available currencies`);

    const fiat = store.fiatCurrencies.find((item) => item === callParameters.fiat);
    !!fiat ? store.setFiatCurrency(fiat) : warn(`Could not set fiat currency`, `${callParameters.fiat} is not found in the available currencies`);
}

function provideOrder(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.ORDER_INFO, ['crypto', 'fiat', 'amount'], callParameters)) {
        return;
    }

    provideCurrencies(callParameters);

    const store = useQuoteStore();

    if (callParameters.type) {
        callParameters.type === QuoteFrom.CRYPTO ? store.setCryptoAmount(callParameters.amount) : store.setFiatAmount(callParameters.amount);
        return;
    }

    if (store.request.amount) {
        store.request.from === QuoteFrom.CRYPTO ? store.setCryptoAmount(store.request.amount) : store.setFiatAmount(store.request.amount);
        return;
    }
}

function provideConfirmedOrder(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.CONFIRMED_ORDER, ['orderId'], callParameters)) {
        return;
    }

    const quoteConfirmationStore = useQuoteConfirmationStore();

    quoteConfirmationStore.setOrderId(callParameters.orderId);
}

function provideWalletAddresses(callParameters: MethodParameters): void {
    const clientInfoStore = useClientInfoStore();
    if (clientInfoStore.client.walletVerificationRequired) {
        return warn('Providing wallet addresses is not allowed', 'When manual address input is allowed no addresses can be provided');
    }

    const configurationStore = useConfigurationStore();
    if (configurationStore.isUnifiedCheckout) {
        return warn('Providing wallet addresses is not allowed', 'Unified Checkout only allows offer-request addresses and saved BTC Direct addresses');
    }

    // TODO: Can't use address because it's typed to string
    if (!validateRequiredParameters(WalletAddressMethodNames.ADDRESSES, ['addresses'], callParameters)) {
        return;
    }

    const addresses = (!Array.isArray(callParameters.addresses) ? [callParameters.addresses] : callParameters.addresses) as ExternalWalletAddress[];

    // TODO: Check if ripple is the only one with a destination tag
    if (addresses.some((address) => address.currency.toUpperCase() !== 'XRP' && !!address.destinationTag)) {
        return warn('Destination tag is not supported for this coin', 'Destination tag can only be provided for Ripple (XRP) addresses');
    }

    if (configurationStore.instanceConfiguration.walletConfirmationRequired && addresses.some((address) => !address.id)) {
        return warn('ID is missing for some wallet addresses', "ID's should be provided for each address when confirming via your platform");
    }

    const walletAddresses = addresses.map((wallet) => convertExternalWalletAddressToInternalWalletAddress(wallet));

    const walletAddressStore = useWalletAddressesStore();

    walletAddressStore.setAddressesAreManuallyProvided(true);
    walletAddressStore.setWalletAddresses(walletAddresses);
}

function provideWalletAddressConfirmation(callParameters: MethodParameters, instanceParameters: InstanceParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.ADDRESS_CONFIRM, ['address'], callParameters)) {
        return;
    }

    // TODO: Should be order store?
    instanceParameters.confirmedWalletAddress = callParameters.address;
}

function provideAdyenParametersToWidget(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.ADYEN_CALLBACK, ['sessionId', 'redirectResult'], callParameters)) {
        return;
    }

    const { sessionId, redirectResult } = callParameters;
    const adyenSessionStore = useAdyenSessionStore();

    adyenSessionStore.setAdyenParameters({ sessionId, redirectResult });
}

function provideWalletAddressForVerification(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(WalletAddressMethodNames.ADDRESS_PROVIDE, ['addresses'], callParameters)) {
        return;
    }

    const address = convertExternalWalletAddressToInternalWalletAddress(
        Array.isArray(callParameters.addresses) ? callParameters.addresses[0] : callParameters.addresses
    );

    // TODO: Check if ripple is the only one with a destination tag
    if (address.currency.toUpperCase() !== 'XRP' && !!address.destinationTag) {
        warn('Destination tag is not supported for this coin', 'Destination tag can only be provided for Ripple (XRP) addresses');
        return;
    }

    const store = useWalletAddressesStore();
    store.setManualAddress(address);
}

function provideSelectedWalletAddress(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(WalletAddressMethodNames.ADDRESS_SELECT, ['address'], callParameters)) {
        return;
    }

    const store = useWalletAddressesStore();

    const foundByIdentifier = store.walletAddresses.find((wallet) => 'identifier' in wallet && wallet.identifier === callParameters.address);
    if (foundByIdentifier) {
        store.selectWalletAddress(foundByIdentifier);
        return;
    }

    const foundByAddress = store.walletAddresses.find((wallet) => wallet.address === callParameters.address);
    if (foundByAddress) {
        store.selectWalletAddress(foundByAddress);
        return;
    }
}

function provideSelectedPaymentMethod(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.PAYMENT_METHOD, ['method'], callParameters)) {
        return;
    }

    const paymentMethodsStore = usePaymentMethodsStore();
    paymentMethodsStore.selectPaymentMethod(callParameters.method);
}

// TODO: Add check on either callbackUrl or callbackUrlF2c (and optionally callbackUrlC2f)
function provideCallbackUrl(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.CALLBACK, ['callbackUrl'], callParameters)) {
        return;
    }

    // TODO: Should be config store
    try {
        new URL(callParameters.callbackUrl);
    } catch (exception) {
        warn('No valid callback URL provided', 'Please provide a valid callback URL or omit the call');
        return;
    }

    const configurationStore = useConfigurationStore();
    configurationStore.setOrderConfiguration({ callbackUrlC2f: callParameters.callbackUrl, callbackUrlF2c: callParameters.callbackUrl });
}

function provideTransactionId(callParameters: MethodParameters, instanceParameters: InstanceParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.TRANSACTION_CONFIRM, ['orderId', 'transactionId'], callParameters)) {
        warn(`Tried to provide a transaction ID with missing data (orderId: ${callParameters.orderId}, transactionId: ${callParameters.transactionId})`);
        return;
    }

    const quoteConfirmationStore = useQuoteConfirmationStore();
    const orderId = quoteConfirmationStore.coinToFiat.orderId;

    if (callParameters.orderId !== orderId) {
        warn(`Tried to provide a transaction ID with other order ID (expected: ${orderId}, received: ${callParameters.orderId})`);
        return;
    }

    // TODO: Move to c2f main?
    instanceParameters.orderId = callParameters.orderId;
    instanceParameters.transactionId = callParameters.transactionId;
}

function provideOfferParameters(callParameters: MethodParameters): void {
    const offerStore = useOfferStore();
    const currenciesStore = useCurrenciesStore();
    const quoteStore = useQuoteStore();
    const paymentMethodsStore = usePaymentMethodsStore();
    const walletAddressStore = useWalletAddressesStore();
    const clientInfoStore = useClientInfoStore();

    const parameters: OfferParameters = getOfferParameters(callParameters);
    if (!parameters || !Object.keys(parameters).length) {
        const message = 'Offer call provided with no correct parameters';
        const details = `Parameters: ${JSON.stringify(callParameters)}`;

        dispatchSentryException(message + ' ' + details);
        warn(message, details);
        return;
    }

    if (!parameters.signature && (!!parameters.walletAddress || !!parameters.returnUrl || !!parameters.callbackUrl)) {
        const message = 'Offer call contained signature but not one of the required parameters [walletAddress, returnUrl, callbackUrl]';
        const details = `Parameters: ${JSON.stringify(callParameters)}`;

        dispatchSentryException(message + ' ' + details);
        warn(message, details);
        return;
    }

    if (!!parameters.baseCurrency) {
        currenciesStore.setCryptoCurrency(parameters.baseCurrency);
    }

    if (!!parameters.quoteCurrency) {
        currenciesStore.setFiatCurrency(parameters.quoteCurrency);
    }

    if (!!parameters.baseCurrencyAmount) {
        quoteStore.setCryptoAmount(parameters.baseCurrencyAmount);
    }

    if (!!parameters.quoteCurrencyAmount) {
        quoteStore.setFiatAmount(parameters.quoteCurrencyAmount);
    }

    if (!!parameters.paymentMethod) {
        paymentMethodsStore.selectPaymentMethod(parameters.paymentMethod);
    }

    if (!!parameters.walletAddress) {
        walletAddressStore.setCurrentWalletAddressType('select');
        walletAddressStore.setAddressesAreManuallyProvided(true);

        const walletAddress: WalletAddress = {
            identifier: `${parameters.walletAddress}-${currenciesStore.selectedCryptoCurrency.code}`,
            address: parameters.walletAddress,
            currency: currenciesStore.selectedCryptoCurrency.code,
            destinationTag: parameters.walletAddressTag ? Number(parameters.walletAddressTag) : null,
            status: null
        };

        if (clientInfoStore.client.walletVerificationRequired) {
            walletAddress.status = 'open' as WalletAddressVerificationStatus;
        }

        walletAddressStore.selectWalletAddress(walletAddress);
    }

    offerStore.setOfferRequest(parameters);

    if (
        !!parameters.baseCurrency &&
        !!parameters.quoteCurrency &&
        (!!parameters.baseCurrencyAmount || !!parameters.quoteCurrencyAmount) &&
        !!parameters.paymentMethod &&
        !!parameters.walletAddress
    ) {
        offerStore.setRequiredOfferParametersProvided(true);
    }
}

function cancelCurrentOrder(callParameters: MethodParameters, instanceParameters: InstanceParameters): void {
    // TODO: Should be order store
    instanceParameters.cancelOrder = Date.now();
}

function cancelWalletAddressConfirmation(callParameters: MethodParameters, instanceParameters: InstanceParameters): void {
    // TODO: Should be order store
    instanceParameters.cancelOrder = Date.now();
}

// TODO: Not needed once stringed destinationTag is removed
function convertExternalWalletAddressToInternalWalletAddress(wallet: ExternalWalletAddress): WalletAddress {
    if (!wallet || !wallet.destinationTag || typeof wallet.destinationTag === 'number') {
        return { ...wallet, identifier: wallet.id } as WalletAddress;
    }

    return {
        address: wallet.address,
        currency: wallet.currency,
        destinationTag: Number(wallet.destinationTag),
        identifier: wallet.id,
        name: wallet.name
    };
}

function attemptDirectConfirm(callParameters: MethodParameters): void {
    if (!validateRequiredParameters(OrderMethodNames.ATTEMPT_DIRECT_CONFIRM, ['enableAttemptDirectConfirm'], callParameters)) {
        return;
    }

    const configurationStore = useConfigurationStore();
    configurationStore.setAttemptDirectConfirm(callParameters.enableAttemptDirectConfirm);
}

/**
 * Deprecated method.
 * To prevent warnings or breaking changes we simply do nothing.
 */
function showAdditionalFees(callParameters: MethodParameters): void {
    // Do nothing
}
